import React, { useEffect, useState } from "react";
import apiClient from "../../lib/api";
import { useI18n } from "../../i18n";
import { toast } from "sonner";
import { Save, Truck } from "lucide-react";

const AdminShipping = () => {
  const { lang } = useI18n();
  const [shipping, setShipping] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { apiClient.get("/admin/settings").then((r) => setShipping(r.data.shipping || {})); }, []);

  if (!shipping) return <div className="text-ink-muted">Loading…</div>;

  const upd = (method, key, val) => setShipping({ ...shipping, [method]: { ...shipping[method], [key]: val } });

  const save = async () => {
    setBusy(true);
    try {
      await apiClient.put("/admin/settings", { shipping });
      toast.success(lang === "nl" ? "Opgeslagen" : "Saved");
    } catch (e) {
      toast.error("Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-shipping-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-light">{lang === "nl" ? "Verzending" : "Shipping"}</h1>
        <button onClick={save} disabled={busy} className="btn-primary text-sm" data-testid="shipping-save"><Save className="w-4 h-4" /> {busy ? "…" : (lang === "nl" ? "Opslaan" : "Save")}</button>
      </div>

      <div className="space-y-4">
        {Object.entries(shipping).map(([key, m]) => (
          <div key={key} className="border border-black/10 bg-white p-6" data-testid={`shipping-method-${key}`}>
            <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-secondary-warm flex items-center justify-center"><Truck className="w-4 h-4 text-terracotta" /></div>
                <div>
                  <div className="font-display text-xl">{m.carrier} — {key}</div>
                  <div className="text-xs text-ink-muted font-mono">{key}</div>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={m.enabled !== false} onChange={(e) => upd(key, "enabled", e.target.checked)} data-testid={`shipping-enabled-${key}`} />
                {m.enabled !== false ? (lang === "nl" ? "Actief" : "Enabled") : (lang === "nl" ? "Uit" : "Disabled")}
              </label>
            </div>
            <div className="grid md:grid-cols-2 gap-3 mb-3">
              <F label={lang === "nl" ? "Label (NL)" : "Label (NL)"} value={m.label_nl} onChange={(v) => upd(key, "label_nl", v)} testid={`shipping-label-nl-${key}`} />
              <F label={lang === "nl" ? "Label (EN)" : "Label (EN)"} value={m.label_en} onChange={(v) => upd(key, "label_en", v)} testid={`shipping-label-en-${key}`} />
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <F label="Carrier" value={m.carrier} onChange={(v) => upd(key, "carrier", v)} testid={`shipping-carrier-${key}`} />
              <F label={lang === "nl" ? "Prijs (€)" : "Price (€)"} type="number" step="0.01" value={m.price} onChange={(v) => upd(key, "price", parseFloat(v) || 0)} testid={`shipping-price-${key}`} />
              <F label={lang === "nl" ? "Gratis vanaf (€) — leeg = nooit gratis" : "Free above (€) — empty = never free"} type="number" step="0.01" value={m.free_above ?? ""} onChange={(v) => upd(key, "free_above", v === "" ? null : parseFloat(v))} testid={`shipping-free-above-${key}`} />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 text-xs text-ink-muted">{lang === "nl" ? "Wijzigingen zijn meteen actief op de afrekenpagina." : "Changes take effect immediately on the checkout page."}</div>
    </div>
  );
};

const F = ({ label, value, onChange, testid, type = "text", step }) => (
  <label className="block">
    <div className="text-xs font-label text-ink-muted mb-1">{label}</div>
    <input data-testid={testid} type={type} step={step} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="border border-black/15 px-3 py-2 text-sm w-full bg-white" />
  </label>
);

export default AdminShipping;
